import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "space-y-2" }
const _hoisted_2 = { class: "relative" }
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = {
  key: 0,
  class: "absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
}
const _hoisted_5 = {
  key: 0,
  class: "px-4 py-3 text-center text-sm text-gray-500"
}
const _hoisted_6 = {
  key: 1,
  class: "px-4 py-3 text-center"
}
const _hoisted_7 = { key: 2 }
const _hoisted_8 = ["onMousedown"]
const _hoisted_9 = { class: "font-medium text-gray-900" }
const _hoisted_10 = { class: "text-sm text-gray-500" }
const _hoisted_11 = {
  key: 3,
  class: "px-4 py-3 text-center text-gray-500"
}
const _hoisted_12 = { class: "w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600" }
const _hoisted_13 = {
  key: 0,
  class: "text-gray-900"
}
const _hoisted_14 = { class: "font-medium" }
const _hoisted_15 = { class: "text-sm text-gray-500" }
const _hoisted_16 = {
  key: 1,
  class: "text-gray-500 italic"
}

import { ref, watch, onMounted, onUnmounted } from 'vue'
import { searchBuyers } from '@/api/buyers'
import { Buyer } from '@/types/buyer'

interface Props {
  modelValue?: number | null
  label?: string
  placeholder?: string
  required?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: number | null): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BuyerAutocomplete',
  props: {
    modelValue: {},
    label: { default: 'Acheteur' },
    placeholder: { default: 'Rechercher un acheteur...' },
    required: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const searchTerm = ref('')
const buyers = ref<Buyer[]>([])
const showDropdown = ref(false)
const selectedBuyer = ref<Buyer | null>(null)
const isLoading = ref(false)
let searchTimeout: ReturnType<typeof setTimeout> | null = null
let requestSeq = 0

async function runSearch(rawTerm: string): Promise<void> {
  const term = rawTerm.trim()
  if (term.length < 2) {
    buyers.value = []
    return
  }

  const seq = ++requestSeq
  isLoading.value = true
  try {
    const results = await searchBuyers(term)
    if (seq !== requestSeq) return
    buyers.value = results
  } catch {
    if (seq !== requestSeq) return
    buyers.value = []
  } finally {
    if (seq === requestSeq) isLoading.value = false
  }
}

function handleInput(): void {
  if (searchTimeout) clearTimeout(searchTimeout)

  showDropdown.value = true
  if (searchTerm.value.trim().length < 2) {
    buyers.value = []
    return
  }

  searchTimeout = setTimeout(() => {
    void runSearch(searchTerm.value)
  }, 300)
}

function handleFocus(): void {
  showDropdown.value = true
  if (searchTerm.value.trim().length >= 2 && buyers.value.length === 0) {
    void runSearch(searchTerm.value)
  }
}

function handleBlur(): void {
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

function selectBuyer(buyer: Buyer): void {
  selectedBuyer.value = buyer
  emit('update:modelValue', buyer.id)
  showDropdown.value = false
  searchTerm.value = ''
  buyers.value = []
}

function clearSelection(): void {
  selectedBuyer.value = null
  searchTerm.value = ''
  buyers.value = []
  emit('update:modelValue', null)
}

function handleClickOutside(event: Event): void {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showDropdown.value = false
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue === null || newValue === undefined) {
      clearSelection()
    } else if (selectedBuyer.value?.id !== newValue) {
      const buyer = buyers.value.find((b) => b.id === newValue)
      if (buyer) selectedBuyer.value = buyer
    }
  },
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (searchTimeout) clearTimeout(searchTimeout)
})

__expose({
  clearSelection,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
        type: "text",
        class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
        placeholder: _ctx.placeholder,
        autocomplete: "off",
        onInput: handleInput,
        onFocus: handleFocus,
        onBlur: handleBlur
      }, null, 40, _hoisted_3), [
        [_vModelText, searchTerm.value]
      ]),
      (showDropdown.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (searchTerm.value.trim().length < 2)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Tapez au moins 2 lettres… "))
              : (isLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[1] || (_cache[1] = [
                    _createElementVNode("div", { class: "flex items-center justify-center space-x-2" }, [
                      _createElementVNode("div", { class: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" }),
                      _createElementVNode("span", { class: "text-sm text-gray-500" }, "Recherche en cours...")
                    ], -1)
                  ])))
                : (buyers.value.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(buyers.value, (buyer) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: buyer.id,
                          class: "px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0",
                          onMousedown: _withModifiers(($event: any) => (selectBuyer(buyer)), ["prevent"])
                        }, [
                          _createElementVNode("div", _hoisted_9, _toDisplayString(buyer.nomSociete), 1),
                          _createElementVNode("div", _hoisted_10, _toDisplayString(buyer.email), 1)
                        ], 40, _hoisted_8))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_11, " Aucun acheteur trouvé "))
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_12, [
      (selectedBuyer.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, _toDisplayString(selectedBuyer.value.nomSociete), 1),
            _createElementVNode("div", _hoisted_15, _toDisplayString(selectedBuyer.value.email), 1)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_16, " Laissez vide si pour tout acheteur "))
    ])
  ]))
}
}

})